/**
 * Document-type field registry — the single source of truth for "what structured
 * fields does document type X expose" so a workflow step can reference them as
 * `${slotKey.field}` template variables.
 *
 * Field names are taken from the actual extraction sources:
 *  - the server-side MRZ scanner (`services/document-processing/src/lib/mrzScan.ts`, run during document
 *    processing) emits identity-document fields (`firstName`, `lastName`, `documentNumber`,
 *    `nationality`, …) into `items.extracted_metadata` (read ad-hoc today in
 *    `replacePartyStep.ts`).
 *  - `items.contract_type` is written by TWO independent classifiers (both in the
 *    docproc service, `services/document-processing/src/`), both feeding the same
 *    column: the image classifier `pipeline/classifyImage.ts`
 *    (`DOCUMENT_CLASSES` → 'identity', 'contract-scan', 'receipt', …) for
 *    photographed uploads, and the entity extractor `lib/entityExtractor.ts`
 *    (`contract_type_detected` enum → 'employment', 'rental', 'services', 'other')
 *    for real ingested documents. A logical type's `contractTypes[]` must therefore
 *    list the raw values from BOTH namespaces that map onto it (e.g. a real "rental"
 *    contract and a scanned 'contract-scan' both map to logical `contract`).
 */
import type { ContractType } from './contractTypes'
import { IMAGE_DOCUMENT_CLASSES } from './contractTypes'
import { identityFrom } from './entities/identityDocument'

/**
 * The file/content modality of a document type — the SSOT for "which steps can
 * operate on this document." `word` = a `.docx` the paragraph-editor can rewrite
 * (replace_party); `image` = a photo/scan (identity document, receipt) that
 * editor cannot touch; `pdf` = a PDF. Before this existed, "rewrite needs a docx"
 * lived ONLY as a magic-byte check in `runner.ts` and was enforced at runtime —
 * so an image-as-edit-source published clean and failed mid-run. Now the contract
 * is data, enforced at authoring + publish (see `STEP_SOURCE_MODALITY`).
 */
export type DocumentModality = 'word' | 'image' | 'pdf'

/**
 * What a workflow slot of this type actually CONSUMES — the axis that decides how a
 * document is judged eligible (MAR-168).
 *
 *  - `'file'`   — the step opens the document's BYTES (convert an image, revise a `.docx`),
 *                 so eligibility is the file format, judged by extension.
 *  - `'fields'` — the step reads the document's EXTRACTED DATA (`items.extracted_metadata`),
 *                 never the file, so eligibility is "was the data extracted", and the file
 *                 format is irrelevant.
 *
 * Before this axis existed, `modality` answered both questions at once: `identity` was
 * gated as if a step were going to open the image — so a scanned **PDF** DUI whose MRZ was
 * fully extracted and indexed was rejected with "no es una imagen" by a workflow that only
 * ever wanted the extracted fields. The extension check was a proxy; the data being present
 * is the real requirement, and each of MAR-38/MAR-131/MAR-168 patched the proxy for one
 * more format. Do not add another extension exception — decide which thing the slot feeds.
 */
export type DocumentSlotFeed = 'file' | 'fields'

export interface DocumentTypeField {
  /** Used in `${slot.<key>}`. */
  key: string
  /** UI label (source-language content lives in the value, not the column name). */
  label: string
  type: 'string' | 'number' | 'date'
  /** `metadata` = read from `extracted_metadata[key]`; `computed` = derived (e.g. fullName). */
  source: 'metadata' | 'computed'
  description?: string
}

export interface DocumentTypeDef {
  /** Logical type id, e.g. `identity`. */
  id: string
  /** UI label, e.g. `Documento de identidad`. */
  label: string
  /** Raw `items.contract_type` values that map onto this logical type. Typed
   *  against `ContractType` so a stale/typo'd value fails to compile. */
  contractTypes: readonly ContractType[]
  /** File/content modality — gates which steps may edit documents of this type. */
  modality: DocumentModality
  /** What a slot of this type feeds a workflow: the file's bytes, or its extracted fields.
   *  Decides HOW eligibility is judged — see `DocumentSlotFeed`. */
  feeds: DocumentSlotFeed
  fields: DocumentTypeField[]
  /** Resolvable but not shown in the builder's type picker (legacy back-compat). */
  hidden?: boolean
}

/** A contract's only scalar field in v1 is its document name (collections —
 *  parties/dates/amounts — are deferred to v2,). */
const CONTRACT_NAME_FIELDS: DocumentTypeField[] = [
  { key: 'name', label: 'Nombre del documento', type: 'string', source: 'computed', description: 'The document name.' },
]

export const DOCUMENT_TYPES: DocumentTypeDef[] = [
  {
    // ONE identity type, for any country and any category of identity document (MAR-176). It was
    // `sv-dui` — "DUI (El Salvador) — reverso" — which fused *what* the document is with *where* it
    // was issued and *which side* was photographed, and so a slot typed `sv-dui` rejected a
    // passport that identifies its holder every bit as validly.
    //
    // Every field comes from the machine-readable zone, read through the one parser
    // (`shared/entities/identityDocument.ts`). A document whose MRZ could not be read yields "" for
    // all of them — that is the readable/unreadable distinction, and it is deliberately NOT a
    // document type: a card front, a glare-ruined scan and a failed passport read are all identity
    // documents that happen to carry no fields.
    id: 'identity',
    label: 'Documento de identidad',
    contractTypes: ['identity'],
    modality: 'image',
    // A workflow never opens the ID's bytes — it reads the fields the MRZ scan extracted. So a
    // scanned PDF (or anything else) with a readable identity is eligible, and a pristine JPG
    // whose MRZ yielded nothing is not (MAR-168).
    feeds: 'fields',
    fields: [
      { key: 'documentNumber', label: 'Número de documento', type: 'string', source: 'metadata', description: 'Identifier from the MRZ.' },
      { key: 'documentLabel', label: 'Tipo de documento', type: 'string', source: 'computed', description: 'What the holder presented, named in prose: "Pasaporte", "Documento de identidad". Country-neutral.' },
      { key: 'issuingCountry', label: 'País emisor', type: 'string', source: 'computed', description: 'ISO alpha-2 country that issued the document ("" if the MRZ omits it).' },
      { key: 'firstName', label: 'Nombres', type: 'string', source: 'metadata' },
      { key: 'lastName', label: 'Apellidos', type: 'string', source: 'metadata' },
      { key: 'fullName', label: 'Nombre completo', type: 'string', source: 'computed', description: 'firstName + " " + lastName.' },
      { key: 'nationality', label: 'Nacionalidad', type: 'string', source: 'metadata' },
      // The MRZ (persisted verbatim as extracted_metadata, persistProcessResult.ts) also carries
      // sex + birthDate — expose them so workflows never re-ask for what the ID already has.
      { key: 'sex', label: 'Sexo (M/F/X)', type: 'string', source: 'metadata', description: 'Sex code from the MRZ.' },
      { key: 'sexLabel', label: 'Sexo', type: 'string', source: 'computed', description: 'M→Masculino, F→Femenino (from the MRZ sex code).' },
      { key: 'birthDate', label: 'Fecha de nacimiento', type: 'string', source: 'metadata', description: 'Birth date from the MRZ.' },
      { key: 'age', label: 'Edad', type: 'string', source: 'computed', description: 'Age in years computed from the MRZ birthDate ("" if unparseable).' },
    ],
  },
  // Granular contract subtypes — match the real ingestion taxonomy
  // (`entityExtractor.ts` → rental/employment/services). A photographed contract is
  // 'contract-scan' only until ingestion reclassifies it to one of these.
  {
    id: 'rental',
    label: 'Contrato de arrendamiento',
    contractTypes: ['rental'],
    modality: 'word',
    feeds: 'file',
    fields: CONTRACT_NAME_FIELDS,
  },
  {
    id: 'employment',
    label: 'Contrato laboral',
    contractTypes: ['employment'],
    modality: 'word',
    feeds: 'file',
    fields: CONTRACT_NAME_FIELDS,
  },
  {
    id: 'services',
    label: 'Contrato de servicios',
    contractTypes: ['services'],
    modality: 'word',
    feeds: 'file',
    fields: CONTRACT_NAME_FIELDS,
  },
  {
    id: 'poder',
    label: 'Poder / Mandato',
    contractTypes: ['poder'],
    modality: 'word',
    feeds: 'file',
    fields: CONTRACT_NAME_FIELDS,
  },
  {
    id: 'receipt',
    label: 'Recibo / Factura',
    contractTypes: ['receipt'],
    modality: 'image',
    feeds: 'file',
    fields: CONTRACT_NAME_FIELDS,
  },
  // Legacy umbrella type — resolvable for existing slots saved with documentType
  // 'contract', but hidden from the picker (new slots choose a granular subtype).
  // Listed LAST so granular subtypes win `documentTypeForContractType`.
  {
    id: 'contract',
    label: 'Contrato',
    contractTypes: ['employment', 'rental', 'services', 'contract-scan'],
    modality: 'word',
    feeds: 'file',
    fields: CONTRACT_NAME_FIELDS,
    hidden: true,
  },
  // Generic IMAGE input — any photographed/scanned upload. Used as a `documentType`
  // constraint to filter a document_select slot to images (e.g. the "Imagen a Word"
  // template). Listed LAST so it never wins `documentTypeForContractType` over the
  // specific image types (identity/receipt) or the hidden `contract` umbrella — it only
  // newly resolves the previously-unowned image classes (photo, other). Hidden from the
  // builder's type picker; resolved only via an explicit `documentType: 'image'` constraint.
  {
    id: 'image',
    label: 'Imagen (foto o escaneo)',
    contractTypes: IMAGE_DOCUMENT_CLASSES,
    modality: 'image',
    feeds: 'file',
    fields: CONTRACT_NAME_FIELDS,
    hidden: true,
  },
  // Generic PDF input — used as a `documentType` constraint to filter a document_select slot
  // to PDFs (the "PDF a Word" template). `contractTypes` is deliberately EMPTY, unlike the
  // image umbrella: a PDF's semantic class is orthogonal to its format (a rental agreement is
  // 'rental' whether it arrives as .docx or .pdf), so claiming contract types here would
  // hijack `documentTypeForContractType` for Word documents. Acceptance is judged by the file
  // name via `isPdfFileName`, the way the image slot gates by `isImageFileName`.
  {
    id: 'pdf',
    label: 'PDF',
    contractTypes: [],
    modality: 'pdf',
    feeds: 'file',
    fields: CONTRACT_NAME_FIELDS,
    hidden: true,
  },
]

export function documentTypeById(id: string | null | undefined): DocumentTypeDef | null {
  if (!id) return null
  return DOCUMENT_TYPES.find(t => t.id === id) ?? null
}

/** Modality of a logical document-type id (e.g. `identity` → `image`). */
export function modalityForDocumentType(id: string | null | undefined): DocumentModality | null {
  return documentTypeById(id)?.modality ?? null
}

/** Raster image file extensions the `image` modality accepts (JPG/PNG and friends). */
export const IMAGE_FILE_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tif', 'tiff', 'heic', 'heif',
] as const

/**
 * Is this a raster image, judged by its file name? The generic `image` slot
 * (e.g. "Imagen a Word") accepts any image regardless of how the classifier
 * labelled its `contract_type`, so acceptance keys off the actual file, not the
 * semantic class.
 */
export function isImageFileName(fileName: string | null | undefined): boolean {
  if (!fileName) return false
  const ext = fileName.split('.').pop()?.toLowerCase()
  return !!ext && (IMAGE_FILE_EXTENSIONS as readonly string[]).includes(ext)
}

/** Word-processing file extensions the paragraph editor (docx service) can revise. */
export const WORD_FILE_EXTENSIONS = ['docx', 'doc'] as const

/**
 * Is this an editable Word document, judged by its file name? A word-modality
 * *edit* step (`replace_party`) can only revise a real
 * `.docx` — a PDF or a photographed scan of a contract crashes the docx service
 * mid-run. So the run-form picker gates those slots by the actual file, the same
 * way the generic image slot gates by `isImageFileName`.
 */
export function isWordFileName(fileName: string | null | undefined): boolean {
  if (!fileName) return false
  const ext = fileName.split('.').pop()?.toLowerCase()
  return !!ext && (WORD_FILE_EXTENSIONS as readonly string[]).includes(ext)
}

/**
 * Is this a PDF, judged by its file name? The generic `pdf` slot ("PDF a Word") accepts any
 * PDF regardless of the classifier's `contract_type` — a PDF may be a rental agreement, a
 * power of attorney or a bare scan, so the semantic class says nothing about the format.
 * Gates by the file, like `isImageFileName`.
 */
export function isPdfFileName(fileName: string | null | undefined): boolean {
  if (!fileName) return false
  return fileName.split('.').pop()?.toLowerCase() === 'pdf'
}

/**
 * Does this document carry the extracted data a `feeds: 'fields'` slot reads? The one
 * eligibility question for data-consuming slots, shared by the entity gate and the run-form
 * picker so they can never disagree (MAR-168). Judged by the ONE parser (`identityFrom`),
 * never by file format and never by the classifier bucket.
 *
 * Only `identity` is a fields-fed type today; a future one must add its own presence check
 * here rather than falling back to `true`.
 */
export function documentSlotDataPresent(typeId: string, extractedMetadata: unknown): boolean {
  if (typeId === 'identity') return identityFrom(extractedMetadata) !== null
  return true
}

export function documentTypeForContractType(ct: string | null | undefined): DocumentTypeDef | null {
  if (!ct) return null
  return DOCUMENT_TYPES.find(t => (t.contractTypes as readonly string[]).includes(ct)) ?? null
}
